import type { GameId } from "../../shared/curriculum";

/**
 * Every image and video the website pages load, and the one place their address
 * is decided.
 *
 * 🔴 ONE BASE, SO MOVING THE ASSETS IS A ONE-LINE CHANGE. They ship in
 * `public/landingpage/`, which Cloudflare Pages serves from its CDN alongside
 * the app — same origin, so the CSP's `'self'` already covers them and nothing
 * here widens it. If the art later moves to a Kalqy-owned R2 bucket, this
 * constant becomes that bucket's public URL and the CSP gains that one host;
 * no component names a path of its own.
 *
 * ⚠️ NOTHING UNDER `landingpage/` IS PRECACHED (see `globIgnores` in
 * `vite.config.ts`). It is website art for a visitor deciding whether to look
 * further, not app shell a classroom needs offline — putting ~16 MB of hero
 * video into every school laptop's first install would cost the app the thing
 * its precache exists to protect.
 */
export const LANDING_ASSET_BASE = "/landingpage";

const at = (path: string): string => `${LANDING_ASSET_BASE}/${path}`;

/**
 * A full-bleed background: a still for the first paint (and for anyone who
 * should not get motion) plus the loop that replaces it.
 *
 * 🔴 THE STILL IS THE LOOP'S OWN SOURCE IMAGE, not a frame grabbed from it.
 * Each loop was animated from exactly this picture, so the swap from poster to
 * video is invisible — the scene simply starts moving.
 */
export interface Backdrop {
  /** 1920 px wide — desktop and anything above a phone. */
  still: string;
  /** 960 px wide — what a phone downloads instead. */
  stillSmall: string;
  /** VP9, listed first; the smaller file wherever it plays. */
  webm: string;
  /** H.264, for Safari and older school browsers. */
  mp4: string;
}

export const HOME_BACKDROP: Backdrop = {
  still: at("home/hero-1920.webp"),
  stillSmall: at("home/hero-960.webp"),
  webm: at("home/hero-loop.webm"),
  mp4: at("home/hero-loop.mp4"),
};

export const HOME_ART = {
  /** Turo, two children, the robot, the lion cub and the parrot — cut out. */
  cast: at("home/cast.webp"),
  turo: at("home/turo.webp"),
  classroom: at("home/classroom.webp"),
  homePlay: at("home/home-play.webp"),
  privacyShield: at("home/privacy-shield.webp"),
  sunset: at("home/sunset-1920.webp"),
  sunsetSmall: at("home/sunset-960.webp"),
  /** The For schools hero — the classroom scene, full width. */
  classroomWide: at("home/classroom-1920.webp"),
  classroomWideSmall: at("home/classroom-960.webp"),
} as const;

/** The game's own painted plate, compressed for a card. */
export function gameCardArt(id: GameId): string {
  return at(`games/${id}-card.webp`);
}

/** The world behind a game page's hero. Only the four games with a page have one. */
export function gameBackdrop(id: GameId): Backdrop {
  return {
    still: at(`games/${id}-hero-1920.webp`),
    stillSmall: at(`games/${id}-hero-960.webp`),
    webm: at(`games/${id}-loop.webm`),
    mp4: at(`games/${id}-loop.mp4`),
  };
}
